// Tìm chức năng (Ctrl K) – hộp lệnh shadcn `CommandDialog`, cùng kiểu tìm chung của customer/web.
// Chỉ tìm trang trong menu tài khoản được vào (thiếu quyền → không có), lọc ngay ở web theo từng từ, không phân biệt dấu.
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { CornerDownLeft, Search } from 'lucide-react'
import { useCurrentUser } from '@modules/auth'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@shared/components/ui/command'
import { cn, foldVi } from '@shared/utils'
import { navPages, type NavPage } from './nav'

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
// mục kết quả: cao 36px, bo góc, nền nhạt khi đang chọn (bàn phím / rê chuột)
const ITEM =
  'items-center rounded-md px-2 py-2 data-[selected=true]:bg-muted data-[selected=true]:text-foreground'

/** Phím trong chân hộp */
function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="num inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-background px-1 text-2xs font-medium text-foreground shadow-sm">
      {children}
    </kbd>
  )
}

/** Nút mở tìm chức năng trên thanh đầu + hộp lệnh */
export function NavSearch({ className }: { className?: string }) {
  const { user } = useCurrentUser()
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [])

  const pages = useMemo(() => navPages(user), [user])
  const words = foldVi(q.trim()).split(/\s+/).filter(Boolean)
  const hits = words.length
    ? pages.filter((p) => {
        const text = foldVi(`${p.group} ${p.label}`)
        return words.every((w) => text.includes(w))
      })
    : pages
  // nhóm theo nhóm menu, giữ thứ tự của menu
  const groups = hits.reduce<{ label: string; items: NavPage[] }[]>((acc, p) => {
    const g = acc.find((x) => x.label === p.group)
    if (g) g.items.push(p)
    else acc.push({ label: p.group, items: [p] })
    return acc
  }, [])

  const go = (to: string) => {
    nav(to)
    setOpen(false)
    setQ('')
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Tìm chức năng"
        className={cn(
          'flex h-8 w-full items-center gap-2 rounded-lg border border-input bg-muted/50 px-2.5 text-note text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
      >
        <Search className="size-4 shrink-0" aria-hidden />
        <span className="truncate">Tìm chức năng…</span>
        <kbd className="num pointer-events-none ml-auto hidden h-5 select-none items-center gap-0.5 rounded border border-border bg-background px-1.5 text-2xs font-medium sm:inline-flex">
          {isMac ? '⌘' : 'Ctrl'} K
        </kbd>
      </button>

      <CommandDialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o)
          if (!o) setQ('')
        }}
        title="Tìm chức năng"
        commandProps={{ shouldFilter: false, className: 'bg-popover' }}
      >
        <div className="px-2 pb-0 pt-2.5">
          <CommandInput
            value={q}
            onValueChange={setQ}
            placeholder="Tìm chức năng: khách hàng, gói, nhật ký…"
            wrapperClassName="h-9 gap-1.5 rounded-lg border border-input bg-muted/40 px-2.5 [&>svg]:size-3.5"
            className="h-full text-note"
          />
        </div>
        <CommandList viewportClassName="max-h-[min(60vh,440px)]" className="px-2 py-1.5">
          {!hits.length && <CommandEmpty>Không thấy chức năng nào khớp.</CommandEmpty>}
          {groups.map((g) => (
            <CommandGroup key={g.label} heading={g.label}>
              {g.items.map(({ to, label, icon: Icon }) => (
                <CommandItem key={to} value={to} onSelect={() => go(to)} className={ITEM}>
                  <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
        <div className="flex items-center gap-4 border-t border-border bg-muted/40 px-3.5 py-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Kbd>
              <CornerDownLeft className="size-3" aria-label="Enter" />
            </Kbd>{' '}
            Mở
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> Di chuyển
          </span>
          <span className="ml-auto flex items-center gap-1.5">
            <Kbd>Esc</Kbd> Đóng
          </span>
        </div>
      </CommandDialog>
    </>
  )
}
